import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useRefresh, useRefreshBusy } from '../context/RefreshContext';
import './PullToRefresh.css';

// ── PULL TO REFRESH ──────────────────────────────────────────────────────────
// Drag down from the top of a page to reload it, as on Instagram. Touch only —
// a mouse has a scrollbar and a keyboard has F5, and a desktop browser already
// owns this gesture on a trackpad.
//
// WHERE IT MUST NOT RUN. The design editor (2D floor plan and 3D views) is one
// big drag surface: panning the plan starts exactly like pulling the page, and
// the canvas would lose the gesture to a page reload — losing unsaved work with
// it. Those routes are excluded outright rather than fought with.
const EXCLUDED = [
  '/app/',        // the editor: floor plan + 3D
  '/try',         // guest editor, same canvas
  '/share/',      // shared project viewer, has the 3D walkthrough
];

// How far to drag before it commits, and how far the indicator may travel.
const THRESHOLD = 72;
const MAX_PULL = 120;
// Below this the drag is treated as an ordinary scroll that happened to start
// at the top, not an intent to refresh.
const START_SLOP = 6;
// Minimum time the spinner stays up once committed, so a fast refetch still
// reads as "it did something" rather than a flicker.
const MIN_SPIN = 550;

// Circumference of the progress circle (r = 15), used to drive the arc via
// stroke-dashoffset. Precomputed: it never changes and it is read on every
// touchmove.
const ARC_LEN = 2 * Math.PI * 15;

// How far the PAGE follows the finger. This is the gap that opens between the
// header and the content, and the disc lives in it.
const PAGE_FOLLOW = 0.7;

// The gap, in px, for a given pull. The disc translates by exactly this too —
// it is parked 46px higher (see `margin-top` in the CSS), so it sits hidden
// behind the header at rest and slides out as the gap grows to hold it.
const gapFor = (pull) => Math.round(pull * PAGE_FOLLOW);

// Opens the gap. Written straight to the DOM rather than through state: this
// runs on every touchmove, and a React render per frame makes a drag feel heavy.
//
// A CUSTOM PROPERTY, not a transform. The CSS uses it twice — to push the
// content down and to pull the sticky header back up by the same amount, so the
// header holds still while the page slides out from under it. Setting the
// transform directly here could only do the first half.
function setGap(px) {
  const el = document.getElementById('ptr-shift');
  if (!el) return;
  if (px <= 0) {
    // Removed, not set to 0. The transform is applied by a `[style*=...]`
    // selector precisely so that it disappears with the property — an identity
    // transform would still make this element the containing block for every
    // fixed-position modal in the app.
    el.style.removeProperty('--ptr-gap');
    el.style.willChange = '';
    return;
  }
  el.style.willChange = 'transform';
  el.style.setProperty('--ptr-gap', `${px}px`);
}

const isExcluded = (path) => EXCLUDED.some((p) => path.startsWith(p));

/**
 * True when the touch began inside something that scrolls and is NOT already at
 * its top — a chat list mid-scroll, say. Pulling there means scrolling that
 * element, never refreshing the page.
 */
function insideScrolledChild(node, stopAt) {
  let el = node;
  while (el && el !== stopAt && el.nodeType === 1) {
    if (el.scrollTop > 0) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') return true;
    }
    el = el.parentElement;
  }
  return false;
}

export default function PullToRefresh({ onRefresh }) {
  // Distance pulled, in px, and whether the refresh has been committed.
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  // True for a refresh started ANYWHERE — the pull below, or tapping the tab
  // you are already on (MobileTabBar). One indicator, both gestures.
  const busy = useRefreshBusy();
  // Eased motion, on only once the finger has left. While it is down the page
  // must track it exactly — easing a live drag feels like lag.
  const [spring, setSpringState] = useState(false);

  // A CLASS, not an inline transition: the header is counter-transformed by a
  // descendant rule, and an inline transition on the wrapper would not reach
  // it — the content would glide back while the header snapped.
  const setSpring = (on) => {
    setSpringState(on);
    document.getElementById('ptr-shift')?.classList.toggle('ptr-shift--spring', on);
  };

  // Everything the gesture needs between events. Refs, not state: these change
  // on every touchmove and re-rendering for each one would drop frames.
  const startY = useRef(0);
  const active = useRef(false);     // a qualifying pull is in progress
  const armed = useRef(false);      // touch began at the top of the page
  // touchend is registered once, so it would otherwise close over the pull
  // distance and the callback as they were on the FIRST render.
  const pullRef = useRef(0);
  pullRef.current = pull;
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;

  useEffect(() => {
    // Touch only. `pointer: coarse` rather than a user-agent sniff — it asks
    // the question that actually matters, and stays right on hybrids.
    if (!window.matchMedia?.('(pointer: coarse)').matches) return undefined;

    const atTop = () => (window.scrollY || document.documentElement.scrollTop || 0) <= 0;

    const onStart = (e) => {
      if (refreshing || e.touches.length !== 1) return;
      // The chat overlay pins the body to stop iOS drifting it; while it is
      // open the page behind is not what the finger is addressing.
      if (document.body.style.position === 'fixed') return;
      if (isExcluded(window.location.pathname)) return;
      if (!atTop()) return;
      if (insideScrolledChild(e.target, document.body)) return;

      armed.current = true;
      active.current = false;
      startY.current = e.touches[0].clientY;
      // No easing while the finger is down — the page must track it exactly.
      setSpring(false);
    };

    const onMove = (e) => {
      if (!armed.current || refreshing) return;
      const dy = e.touches[0].clientY - startY.current;

      // Upward, or the page got scrolled under us — this is a scroll.
      if (dy <= 0 || !atTop()) { armed.current = false; setPull(0); setGap(0); return; }
      if (dy < START_SLOP) return;

      active.current = true;
      // Resisted, not 1:1 — the indicator slows as it approaches the limit, so
      // the gesture has a felt end rather than running off the screen.
      const next = Math.min(MAX_PULL, dy * 0.5);
      setPull(next);
      setGap(gapFor(next));

      // Only once we own the gesture, and only if the listener is cancelable:
      // this suppresses the browser's own rubber-band so the two do not fight.
      if (e.cancelable) e.preventDefault();
    };

    const onEnd = () => {
      if (!armed.current) return;
      armed.current = false;
      const committed = active.current && pullRef.current >= THRESHOLD;
      active.current = false;

      // Eased from here on: the finger is gone, so the page and the disc
      // travel on their own and should glide rather than snap.
      setSpring(true);

      if (!committed) { setPull(0); setGap(0); return; }

      setRefreshing(true);
      // Settles back to exactly the threshold and holds there while it works —
      // the same resting position every time, whether you pulled 80px or 200.
      setPull(THRESHOLD);
      setGap(gapFor(THRESHOLD));

      // Runs the actual re-fetch and closes when it finishes. Held open for at
      // least MIN_SPIN either way: a refresh that resolves in 40ms would
      // otherwise flash and leave the user unsure anything happened, and an
      // instant snap-back reads as a dropped gesture.
      const started = Date.now();
      Promise.resolve()
        .then(() => refreshRef.current())
        .catch((err) => { console.warn('[refresh] failed:', err); })
        .then(() => {
          const wait = Math.max(0, MIN_SPIN - (Date.now() - started));
          window.setTimeout(() => {
            setSpring(true);
            setRefreshing(false);
            setPull(0);
            setGap(0);
          }, wait);
        });
    };

    // passive:false is REQUIRED on touchmove — preventDefault is ignored on a
    // passive listener, which is the default for touch events in Chrome.
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
      // Leaving a transform behind would make #ptr-shift a containing block for
      // every fixed-position modal underneath it.
      setGap(0);
      document.getElementById('ptr-shift')?.classList.remove('ptr-shift--spring');
    };
  }, [refreshing]);

  // BUSY FROM ANY TRIGGER, not just this gesture. Tapping the tab you are
  // already on runs the same refresh, and it deserves the same indicator —
  // otherwise the feed silently changes under you.
  const working = refreshing || busy;
  if (!pull && !working) return null;

  const progress = Math.min(1, pull / THRESHOLD);
  const ready = progress >= 1;
  // A refresh nobody pulled for has no finger to follow, so the disc drops to
  // its resting place on its own instead of tracking a gesture that never
  // happened.
  const drop = gapFor(pull || THRESHOLD);

  return (
    <div
      className={`ptr${ready ? ' ptr--ready' : ''}${working ? ' ptr--busy' : ''}${(spring || (!pull && working)) ? ' ptr--spring' : ''}`}
      style={{
        // Same distance the content moved, so the disc keeps its place in the
        // gap for the whole pull rather than drifting across it.
        transform: `translate3d(-50%, ${drop}px, 0)`,
        // Fades and grows in with the pull instead of popping into existence at
        // full size — the indicator arrives WITH the finger, not after it.
        opacity: pull ? Math.min(1, progress * 1.6) : 1,
        '--ptr-scale': pull ? (0.65 + 0.35 * progress).toFixed(3) : '1',
      }}
      aria-hidden="true"
    >
      <span className="ptr-disc">
        {/* An arc that fills as you pull, rather than an arrow that spins.
            The ring closing IS the threshold — you can see how much further to
            go, which an arrow can only hint at. Once it commits, the same arc
            shortens to a quarter and spins as an ordinary loader, so the shape
            never changes and the transition reads as one continuous object. */}
        <svg className="ptr-svg" viewBox="0 0 36 36" width="36" height="36" fill="none">
          <circle className="ptr-track" cx="18" cy="18" r="15" strokeWidth="2.5" />
          <circle
            className="ptr-arc"
            cx="18" cy="18" r="15"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={ARC_LEN}
            strokeDashoffset={working ? ARC_LEN * 0.72 : ARC_LEN * (1 - progress)}
          />
        </svg>
      </span>
    </div>
  );
}

// ── HOST ─────────────────────────────────────────────────────────────────────
// Connects the gesture to the refresh registry. Split out so PullToRefresh
// itself stays a dumb, testable "call this when pulled" component with no
// knowledge of where the data comes from.
export function PullToRefreshHost() {
  const refreshAll = useRefresh();

  const onRefresh = useCallback(async () => {
    // False means no page on screen registered a loader. Reloading is the
    // honest fallback: better a slow refresh than a gesture that silently does
    // nothing on a page nobody has wired up yet.
    const handled = await refreshAll();
    if (!handled) window.location.reload();
  }, [refreshAll]);

  return <PullToRefresh onRefresh={onRefresh} />;
}
